import { useEffect, useState } from 'react';
import { Button, Tab, Tabs } from '@material-ui/core';
import { Link } from '@backstage/core-components';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  displayDate,
  HomeContent,
  localDateKey,
  safeExternalUrl,
  upcomingEvents,
} from './contentModel';
import { homeContentApiRef } from './api';
import { SectionIntro } from '@univerus/plugin-products';
import { useHomeStyles } from './homeStyles';
import { ScrollTrackControls, useScrollTrack } from './ScrollTrack';

// Fetched through the backend proxy as a data: URL, so the browser never
// contacts the image host (TD 014); the placeholder shows until it loads.
function ContentImage({ src }: { src?: string }) {
  const classes = useHomeStyles();
  const homeContentApi = useApi(homeContentApiRef);
  const [url, setUrl] = useState<string>();
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    if (!src) return undefined;
    let active = true;
    homeContentApi
      .getImage(src)
      .then(data => {
        if (active) setUrl(data);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [homeContentApi, src]);
  return (
    <div className={classes.newsImage} aria-hidden="true">
      {url && !failed ? (
        <img
          src={url}
          alt=""
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>UNIVERUS</span>
      )}
    </div>
  );
}
export function LatestContent() {
  const classes = useHomeStyles();
  // A SharePoint sharing link, so it comes from Key Vault through config, never
  // from source; the calendar link is hidden until a real URL is set.
  const calendarUrl = safeExternalUrl(
    useApi(configApiRef).getOptionalString('app.eventsCalendarUrl'),
  );
  const homeContentApi = useApi(homeContentApiRef);
  const [tab, setTab] = useState('blog');
  const [content, setContent] = useState<HomeContent>();
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [today, setToday] = useState(localDateKey);
  const track = useScrollTrack();
  useEffect(() => {
    const update = () => setToday(localDateKey());
    const timer = window.setInterval(update, 60000);
    window.addEventListener('focus', update);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', update);
    };
  }, []);
  useEffect(() => {
    let active = true;
    setFailed(false);
    homeContentApi
      .getContent()
      .then(data => {
        if (active) setContent(data);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [homeContentApi, attempt]);
  const isBlog = tab === 'blog';
  const isEvents = tab === 'events';
  const sources = {
    blog: {
      url: 'https://www.univerus.com/news',
      label: 'All articles',
      updated: content?.blogUpdated,
    },
    social: {
      url: 'https://www.linkedin.com/company/univerus',
      label: 'View on LinkedIn',
      updated: content?.socialUpdated,
    },
    events: {
      url: calendarUrl,
      label: 'Open event calendar',
      updated: content?.eventsUpdated,
    },
  };
  const source = sources[tab as keyof typeof sources];
  const events = content ? upcomingEvents(content.events, today) : [];
  const posts = content
    ? [...(isBlog ? content.posts : content.socialPosts)].sort((a, b) =>
        b.published.localeCompare(a.published),
      )
    : [];
  const kind = isBlog ? 'Blog' : 'LinkedIn';
  const noun = isBlog ? 'articles' : 'posts';
  const updated = source.updated;
  return (
    <section className={classes.section} aria-labelledby="latest-title">
      <div className={classes.sectionHead}>
        <SectionIntro size="section" id="latest-title" title="News & updates">
          The latest from across Univerus.
        </SectionIntro>
      </div>
      <Tabs
        value={tab}
        onChange={(_, value) => setTab(value)}
        className={classes.tabs}
        classes={{ indicator: classes.tabIndicatorHidden }}
        textColor="primary"
        aria-label="Company content"
      >
        <Tab
          value="blog"
          label="Blog"
          id="home-blog-tab"
          aria-controls={tab === 'blog' ? 'home-blog-panel' : undefined}
        />
        <Tab
          value="social"
          label="LinkedIn"
          id="home-social-tab"
          aria-controls={tab === 'social' ? 'home-social-panel' : undefined}
        />
        <Tab
          value="events"
          label="Tradeshows & events"
          id="home-events-tab"
          aria-controls={tab === 'events' ? 'home-events-panel' : undefined}
        />
      </Tabs>
      <div
        role="tabpanel"
        id={`home-${tab}-panel`}
        aria-labelledby={`home-${tab}-tab`}
      >
        {!content && !failed && <p role="status">Loading company content…</p>}
        {failed && (
          <div role="status">
            <p>
              Company content could not be loaded. You can still open the
              original source below.
            </p>
            <Button onClick={() => setAttempt(value => value + 1)}>
              Try again
            </Button>
          </div>
        )}
        {content && !isEvents && posts.length > 0 && (
          <>
            <ScrollTrackControls
              label={`Browse ${posts.length} ${noun}`}
              noun={noun}
              trackId="home-news-track"
              edges={track.edges}
              step={track.step}
            />
            {/* Keyed per tab, so switching tabs starts the track at the
                newest post. */}
            <div
              key={tab}
              id="home-news-track"
              ref={track.ref}
              className={`${classes.scrollTrack} ${classes.newsTrack}`}
              role="region"
              aria-label={`Latest ${kind} ${noun}`}
            >
              {posts.map(post => (
                <article className={classes.newsCard} key={post.url}>
                  <ContentImage key={post.image} src={post.image} />
                  <div className={classes.newsBody}>
                    <span className={classes.cardEyebrow}>{kind}</span>
                    {/* The title link covers the whole card. */}
                    <h3 className={classes.feedTitle}>
                      <Link
                        to={safeExternalUrl(post.url) || source.url || '/'}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-stretched
                      >
                        {post.title}
                      </Link>
                    </h3>
                    {'description' in post && (
                      <p className={classes.postDescription}>
                        {String(post.description)}
                      </p>
                    )}
                    <div className={classes.cardFooter}>
                      <span aria-hidden="true">
                        Read more <span data-arrow>↗</span>
                      </span>
                      <time className={classes.muted} dateTime={post.published}>
                        {displayDate(post.published)}
                      </time>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
        {content && isEvents && events.length > 0 && (
          <div className={classes.feed}>
            {events.map(event => {
              const url = safeExternalUrl(event.links?.[0]);
              return (
                <article className={classes.feedRow} key={event.id}>
                  <time className={classes.muted} dateTime={event.start!}>
                    {displayDate(event.start!)}
                    {event.end && event.end !== event.start && (
                      <> – {displayDate(event.end)}</>
                    )}
                  </time>
                  <div>
                    <h3 className={classes.feedTitle}>
                      {url ? (
                        <Link
                          to={url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {event.name}
                          <span aria-hidden="true"> ↗</span>
                        </Link>
                      ) : (
                        event.name
                      )}
                    </h3>
                    <span className={classes.muted}>
                      {[event.businessUnitRaw, event.location]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
        {content && !(isEvents ? events.length : posts.length) && (
          <p>
            {isEvents
              ? `No upcoming events in this capture.${
                  calendarUrl ? ' Check the calendar for updates.' : ''
                }`
              : 'No posts in this capture.'}
          </p>
        )}
      </div>
      <div className={classes.feedFooter}>
        <span className={classes.muted}>
          {updated
            ? `Snapshot updated ${displayDate(updated)}`
            : 'Official company sources'}
        </span>
        {source.url && (
          <Link to={source.url} target="_blank" rel="noopener noreferrer">
            {source.label} ↗
          </Link>
        )}
      </div>
    </section>
  );
}
